import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoHomeTarget, type RecoHomeTarget } from '@/hooks/useRecoHomeTarget';

// Bug Sesión #98 (extendido) — mismo hueco que ya se cerró en /app y en el comodín /app/* vía
// `LandingDecision` (ver ese archivo), pero seguía abierto en las rutas FIJAS: un bookmark o el
// tab de Teams que apunta directo a /app/reconocer o /app/brillamos se saltaba esa pantalla por
// completo y mostraba lo que la URL pedía, sin importar la campaña activa. Solo estas dos: el
// resto de rutas de /app/* (perfil, admin, innovación...) no tienen un "equivalente en la otra
// pantalla" al que redirigir.
const FIXED_HOME_ROUTES: readonly RecoHomeTarget[] = ['/app/reconocer', '/app/brillamos'];

/**
 * Reportado como "sigue fallando" (2026-08-21) pese a que la redirección en sí ya funcionaba en
 * los dos sentidos: QA no se refería a que NO redirigiera, sino a que redirigía TARDE — el
 * `<Outlet/>` de la ruta equivocada llegaba a montar y pintar un instante ANTES de que el
 * `useEffect` disparara `navigate()`, así que se veía un parpadeo de Reconocer antes de Brillamos
 * (o viceversa).
 *
 * El arreglo NO depende de mover `navigate()` a un `useLayoutEffect` — probado y descartado: en
 * jsdom/Testing Library el cambio de ubicación que dispara `navigate()` dentro de un
 * `useLayoutEffect` no se refleja de forma fiable dentro del mismo `act()` (las 2 pruebas que
 * verifican la redirección quedaban en flaky/timeout con `findByText`), así que se quedó en
 * `useEffect` normal. Lo que sí importa es que `AppLayout` deje de montar el `<Outlet/>` de la
 * ruta pedida por la URL MIENTRAS `isResolvingHome` sea `true` (ver más abajo): la pantalla
 * equivocada nunca llega a existir en el árbol, así que no hay nada que pintar antes del
 * `navigate()`, sin importar en qué momento exacto corra el efecto.
 *
 * Redirige UNA sola vez, al montar, si la URL de entrada es una de las dos rutas fijas de arriba
 * y no coincide con la que corresponde según la campaña activa. Nunca vuelve a evaluarse después
 * — a propósito: si se re-evaluara en cada navegación, el usuario perdería la posibilidad de
 * moverse manualmente por el sidebar entre Fundamentales y Brillamos durante una campaña activa,
 * rompiendo entre otras cosas el check "enviar también a Brillamos" de Reconocer (T5), que solo
 * vive en /app/reconocer.
 *
 * Se llama desde `AppLayout`, que no se desmonta al navegar entre rutas internas de /app/*
 * (React Router solo cambia el `<Outlet />`) — por eso el efecto de este hook corre una única vez
 * por entrada real a la app, no por cada clic del sidebar.
 */
export function useFixedHomeEntryGuard(): { isResolvingHome: boolean } {
  const location = useLocation();
  const navigate = useNavigate();
  const entryPathnameRef = useRef(location.pathname);
  const hasSettledRef = useRef(false);
  const entryPathname = entryPathnameRef.current;
  const isFixedHomeRoute = (FIXED_HOME_ROUTES as readonly string[]).includes(entryPathname);
  const { recoTarget, isLoading } = useRecoHomeTarget();

  // Bloquea el `<Outlet/>` mientras: (a) todavía no se resuelve la campaña activa, o (b) ya se
  // resolvió y no coincide con la URL de entrada — en ambos casos falta la llamada a `navigate()`
  // de abajo, que solo corre una vez que el commit de ESTE render ya pasó. El `|| isLoading` es
  // necesario aparte de comparar contra `recoTarget`: mientras carga, `useRecoHomeTarget` ya
  // devuelve un `recoTarget` por defecto (Reconocimiento) que podría coincidir por pura
  // casualidad con la URL de entrada, sin que eso signifique que ya se sabe la respuesta real.
  const isResolvingHome = !hasSettledRef.current
    && isFixedHomeRoute
    && (isLoading || entryPathname !== recoTarget);

  useEffect(() => {
    if (hasSettledRef.current) return;
    if (!isFixedHomeRoute) { hasSettledRef.current = true; return; }
    if (isLoading) return;
    hasSettledRef.current = true;
    if (entryPathname !== recoTarget) navigate(recoTarget, { replace: true });
  }, [isFixedHomeRoute, isLoading, entryPathname, recoTarget, navigate]);

  return { isResolvingHome };
}
